/**
 * Helpers shared by the admin dashboard widgets. They live here rather than in
 * the global helpers because they only make sense against the shape returned by
 * `/organization/dashboard`.
 */

export const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * The API only returns days that actually have data, so a sparse object would
 * draw a chart with the gaps closed up and make a quiet week look busy. This
 * fills every day of the range with an explicit zero.
 */
export const buildDailySeries = ({ from, to }, values = {}) => {
  const series = {};

  if (!from || !to) return series;

  const cursor = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);

  while (cursor <= end) {
    const date = formatLocalDate(cursor);
    series[date] = values[date] ?? 0;
    cursor.setDate(cursor.getDate() + 1);
  }

  return series;
};

/** Inclusive day count of the selected range, used to size chart ticks. */
export const rangeLength = ({ from, to }) => {
  if (!from || !to) return 7;

  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);

  return Math.max(1, Math.round((end - start) / 86400000) + 1);
};

/**
 * Percentage change against the previous window. Returns null when there is
 * nothing to compare against — showing "+100%" because last week was zero is
 * worse than showing no badge at all.
 */
export const percentDelta = (current, previous) => {
  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined ||
    previous === 0
  ) {
    return null;
  }

  return ((current - previous) / previous) * 100;
};

/** Difference in percentage points, for metrics that are already a percentage. */
export const pointDelta = (current, previous) => {
  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined
  ) {
    return null;
  }

  return current - previous;
};

export const secondsToHours = (seconds) =>
  Math.round(((seconds ?? 0) / 3600) * 10) / 10;

export const faDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("fa-IR");
